import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { STATUS_OPTIONS } from "@/components/Badges";
import { usePriorities } from "@/context/PrioritiesContext";
import { Search, Trash2, MoreHorizontal, X, UserCog, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePageExport } from "@/context/ExportContext";
import SavedViews from "@/components/SavedViews";
import TicketRow from "@/components/tickets/TicketRow";
import { sortTickets } from "@/lib/sortUtils";

const DEFAULT_FILTERS = { status: "all", priority: "all", channel: "all", q: "" };
const SORT_STORAGE_KEY = "kippu_tickets_sort_v1";

export default function Tickets() {
  const { user, hasPerm } = useAuth();
  const { priorityOptions: PRIORITY_OPTIONS } = usePriorities();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [agents, setAgents] = useState([]);
  // Sort state persists within the Tickets module (localStorage).
  const [sort, setSort] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY));
      if (s && s.key && (s.dir === "asc" || s.dir === "desc")) return s;
    } catch { /* ignore */ }
    return { key: "created_at", dir: "desc" };
  });
  useEffect(() => {
    try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort)); } catch { /* ignore */ }
  }, [sort]);

  const toggleSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
  ));
  // Seed filter state from URL params so drill-through links land filtered.
  const [filters, setFilters] = useState(() => ({
    status: searchParams.get("status") || "all",
    priority: searchParams.get("priority") || "all",
    channel: searchParams.get("channel") || "all",
    q: searchParams.get("q") || "",
    // Drill-through extras (passed straight through to the backend list query)
    department_id: searchParams.get("department_id") || "",
    owner_email: searchParams.get("owner_email") || "",
    date_from: searchParams.get("date_from") || "",
    date_to: searchParams.get("date_to") || "",
  }));
  const [selected, setSelected] = useState(new Set());

  const canViewAssignee = hasPerm("tickets.view_assignee");
  const canBulkUpdate = hasPerm("bulk.update_tickets");
  const canBulkAssign = hasPerm("bulk.reassign_tickets") || hasPerm("tickets.assign");
  const canBulkClose = hasPerm("bulk.close_tickets");
  const canBulkCancel = hasPerm("bulk.cancel_tickets");
  const canBulkDelete = hasPerm("delete_ticket");
  const canBulk = canBulkUpdate || canBulkAssign || canBulkClose || canBulkCancel || canBulkDelete;
  const colSpan = 9 + (canViewAssignee ? 1 : 0) + (canBulk ? 1 : 0);

  useEffect(() => {
    api.get("/channels").then((r) => setChannels(r.data));
    api.get("/users").then((r) => setAgents(r.data.filter((u) => u.active)));
  }, []);

  const load = () => {
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.priority !== "all") params.priority = filters.priority;
    if (filters.channel !== "all") params.channel = filters.channel;
    if (filters.q) params.q = filters.q;
    // Drill-through extras
    if (filters.department_id) params.department_id = filters.department_id;
    if (filters.owner_email) params.owner_email = filters.owner_email;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    api.get("/tickets", { params }).then((r) => {
      setTickets(r.data);
      setSelected(new Set());
    });
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Register CSV export for this page
  const exportColumns = useMemo(() => [
    { key: "code", header: "Ticket Code" },
    { key: "subject", header: "Subject" },
    { key: "customer_name", header: "Customer" },
    { key: "channel", header: "Channel" },
    { key: "category", header: "Category" },
    { key: "subcategory", header: "Subcategory" },
    { key: "priority", header: "Priority" },
    { key: "status", header: "Status" },
    { key: "owner_email", header: "Owner" },
    { key: "created_at", header: "Created" },
  ], []);
  usePageExport({ filename: "kippu-tickets.csv", rows: tickets, columns: exportColumns, label: "Tickets" });

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

  // Rank map for meaningful priority ordering (critical → low per master order).
  const priorityRank = useMemo(() => {
    const m = {};
    PRIORITY_OPTIONS.forEach((p, i) => { m[p.value] = i; });
    return m;
  }, [PRIORITY_OPTIONS]);

  // Client-side sort that composes cleanly with filters + saved views.
  const sortedTickets = useMemo(
    () => sortTickets(tickets, sort, priorityRank),
    [tickets, sort, priorityRank],
  );

  const allChecked = tickets.length > 0 && selected.size === tickets.length;
  const someChecked = selected.size > 0 && selected.size < tickets.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(tickets.map((t) => t.id)));
  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const bulk = async (action, value) => {
    if (selected.size === 0) return;
    try {
      const { data } = await api.post("/tickets/bulk-action", {
        ticket_ids: Array.from(selected), action, value,
      });
      toast.success(`${data.updated} ticket${data.updated === 1 ? "" : "s"} ${action === "delete" ? "deleted" : "updated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} ticket(s) permanently?`)) return;
    bulk("delete", null);
  };

  // Reusable sortable column header.
  const SortHead = ({ col, label, align }) => {
    const active = sort.key === col;
    const Icon = !active ? ChevronsUpDown : (sort.dir === "asc" ? ArrowUp : ArrowDown);
    return (
      <TableHead className={align === "right" ? "text-right" : undefined}>
        <button
          type="button"
          onClick={() => toggleSort(col)}
          data-testid={`sort-${col}`}
          aria-label={`Sort by ${label}`}
          className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-slate-700 ${active ? "text-slate-800 font-semibold" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        >
          {label}
          <Icon className={`w-3 h-3 ${active ? "opacity-90" : "opacity-30"}`} strokeWidth={2.4} />
        </button>
      </TableHead>
    );
  };

  return (
    <div className="space-y-5" data-testid="tickets-page">
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="tickets-search" value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search by subject, customer or ticket code..." className="pl-9" />
          </div>

          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-[160px]" data-testid="tickets-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
            <SelectTrigger className="w-[160px]" data-testid="tickets-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.channel} onValueChange={(v) => setFilters({ ...filters, channel: v })}>
            <SelectTrigger className="w-[180px]" data-testid="tickets-filter-channel"><SelectValue placeholder="Channel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              {channels.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <SavedViews
            storageKey="kippu_views_tickets_v1"
            currentFilters={filters}
            onApply={(f) => setFilters({ ...DEFAULT_FILTERS, ...f })}
          />
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selected.size > 0 && canBulk && (
        <div
          data-testid="bulk-action-bar"
          className="bg-slate-900 text-white rounded-lg px-5 py-3 flex items-center justify-between shadow-md"
        >
          <div className="text-sm">
            <span className="font-semibold">{selected.size}</span> ticket{selected.size === 1 ? "" : "s"} selected
            <button onClick={() => setSelected(new Set())} className="ml-3 text-slate-400 hover:text-white text-xs">
              <X className="w-3.5 h-3.5 inline mr-1" />Clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="secondary" data-testid="bulk-action-menu">
                  <MoreHorizontal className="w-4 h-4 mr-1.5" /> Actions
                </Button>
              </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Bulk action</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {canBulkUpdate && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>Set status</DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {STATUS_OPTIONS.map((s) => (
                          <DropdownMenuItem key={s.value} onClick={() => bulk("update_status", s.value)} data-testid={`bulk-status-${s.value}`}>
                            {s.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {canBulkUpdate && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>Set priority</DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {PRIORITY_OPTIONS.map((p) => (
                          <DropdownMenuItem key={p.value} onClick={() => bulk("update_priority", p.value)} data-testid={`bulk-priority-${p.value}`}>
                            {p.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {canBulkAssign && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger><UserCog className="w-3.5 h-3.5 mr-2" />Assign to</DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="max-h-64 overflow-auto">
                        {agents.map((a) => (
                          <DropdownMenuItem key={a.email} onClick={() => bulk("assign", a.email)} data-testid={`bulk-assign-${a.email}`}>
                            {a.name} <span className="text-slate-400 ml-1.5 text-xs">({a.email})</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  )}
                  {canBulkDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={bulkDelete} className="text-rose-600 focus:text-rose-700" data-testid="bulk-delete">
                        <Trash2 className="w-3.5 h-3.5 mr-2" />Delete selected
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                {canBulk && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allChecked || (someChecked ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                )}
                <SortHead col="code" label="ID" />
                <TableHead>Subject</TableHead>
                <SortHead col="customer_name" label="Customer" />
                <TableHead>Channel</TableHead>
                <TableHead>Category</TableHead>
                <SortHead col="department_name" label="Department" />
                <SortHead col="priority" label="Priority" />
                <SortHead col="status" label="Status" />
                {canViewAssignee && <SortHead col="owner_email" label="Owner" />}
                <SortHead col="created_at" label="Created" align="right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTickets.map((t) => (
                <TicketRow
                  key={t.id}
                  t={t}
                  channels={channels}
                  canBulk={canBulk}
                  canViewAssignee={canViewAssignee}
                  selected={selected.has(t.id)}
                  onToggle={toggle}
                  fmt={fmt}
                />
              ))}
              {tickets.length === 0 && (
                <TableRow><TableCell colSpan={colSpan} className="text-center text-sm text-slate-400 py-12">No tickets match these filters</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
